import React, { useContext, useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    LanguageBaseContext,
    havePermissions,
    fetchData,
    HTTP_STATUS,
    DmsAlertWarning,
} from "dmsbasereact";
import { Typography, Stack, IconButton, Tooltip } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatCnpj } from "../../utils/formatters";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { ReportFilters } from "../ReportsPage/components/ReportFilters";
import { useReport } from "../ReportsPage/components/useReport";
import { Permissions } from "../../constants/Permissions";
import { ExpandableChipList } from "../../components/ExpandableChipList/ExpandableChipList";

export const PartnerV2Page = () => {
    const navigate = useNavigate();
    const { language } = useContext(LanguageBaseContext);

    const isReadOnly = !havePermissions([Permissions.PARTNER_LINK_CREATE]);
    const [institutionNotFound, setInstitutionNotFound] = useState(false);

    useEffect(() => {
        if (!isReadOnly) return;
        fetchData("/get/organization_branch/by_person_pk", "GET", true).then((response) => {
            if (response.status !== HTTP_STATUS.OK_200) {
                setInstitutionNotFound(true);
                return;
            }
            const data = response.data?.data;
            const institutions = data?.institutions || (data?.institution ? [data.institution] : []);
            const distributor = institutions[0];
            if (distributor?.organization_branch_pk) {
                navigate(
                    `${APP_ROUTES.ReportsDistributorClients}/${distributor.organization_branch_pk}`,
                    {
                        replace: true,
                        state: {
                            distributorName: distributor.organization_branch?.name,
                            distributorCnpj: distributor.organization_branch?.cnpj,
                            distributorIsPublic: false,
                        },
                    }
                );
            } else {
                setInstitutionNotFound(true);
            }
        });
    }, [isReadOnly, navigate]);

    const handleNavigateToDetail = (row) => {
        navigate(`${APP_ROUTES.ReportsDistributorClients}/${row.organization_branch_pk}`, {
            state: {
                distributorName: row.name,
                distributorCnpj: row.organization_branch?.cnpj,
                distributorIsPublic: !!row.is_public,
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const filterFields = useMemo(() => [
        {
            name: "name",
            label: intl.get("DistributorName").d("Distributor Name"),
            type: "text",
        },
        {
            name: "cnpj",
            label: intl.get("CNPJ").d("CNPJ"),
            type: "text",
        },
        {
            name: "client_names",
            label: intl.get("Partners").d("Partners"),
            type: "text",
        },
    ], [language]);

    const {
        state: { tableRef, filters, clearKey, filterFields: processedFilterFields, columnVisibility },
        handlers: {
            setFilters,
            handleApplyFilters,
            handleClearFilters,
            handleRefresh,
            buildPreFilter,
            buildExportUrl,
            setColumnVisibility,
        },
    } = useReport({
        exportUrl: "/partner/v2/export",
        filterFields,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const header = useMemo(() => [
        {
            label: intl.get("DistributorName").d("Distributor Name"),
            column_name: "name",
            sortable: true,
            width: "200px",
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "cnpj",
            sortable: true,
            width: "160px",
            function: (value) => formatCnpj(value),
        },
        {
            label: intl.get("City").d("City"),
            column_name: "organization_branch",
            sortable: false,
            width: "140px",
            function: (value) => value?.address?.city || "-",
        },
        {
            label: intl.get("State").d("State"),
            column_name: "organization_branch",
            sortable: false,
            width: "80px",
            function: (value) => value?.address?.state || "-",
        },
        {
            label: intl.get("Partners").d("Partners"),
            column_name: "client_names",
            sortable: false,
            width: "400px",
            element: (row) => (
                <ExpandableChipList items={row.client_names || []} maxVisible={6} />
            ),
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            width: "80px",
            element: (row) => (
                <Tooltip title={intl.get("LinkPartners").d("Link Partners")}>
                    <IconButton
                        size="small"
                        onClick={() => handleNavigateToDetail(row)}
                        sx={{
                            backgroundColor: "success.main",
                            color: "#fff",
                            "&:hover": { backgroundColor: "success.dark" },
                        }}
                    >
                        <ArrowForwardIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ], [language]);

    if (isReadOnly && institutionNotFound) {
        return (
            <DmsPage name="PartnerV2" message={intl.get("PartnerV2Page.PageAccess").d("User accessed Partners by Distributor page")}>
                <DmsAlertWarning message={intl.get("NoInstitutionLinked").d("No institution linked to your account.")} />
            </DmsPage>
        );
    }

    return (
        <DmsPage name="PartnerV2" message={intl.get("PartnerV2Page.PageAccess").d("User accessed Partners by Distributor page")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                            {intl.get("PartnersByDistributor").d("Partners by Distributor")}
                        </Typography>
                        <ReportFilters
                            filterFields={processedFilterFields}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onApply={handleApplyFilters}
                            onClear={handleClearFilters}
                            onRefresh={handleRefresh}
                            exportUrl={buildExportUrl()}
                            columns={header}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={setColumnVisibility}
                            showPdf={true}
                            showDateRange={false}
                        />
                    </Stack>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        key={`partner-v2-table-${clearKey}`}
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={tableRef}
                        url="/partner/v2/table"
                        pre_filter={buildPreFilter()}
                        toolbar={<></>}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

export default PartnerV2Page;
